import { useCallback, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { listVoices, type Recording } from "../api";
import { useT } from "../i18n";
import { useDismiss } from "../components/useDismiss";

/**
 * RecordingBadge says how the audio being played was made: the speech model,
 * the voice, and the settings it was recorded with.
 *
 * A badge that opens, not a line of text under the title. Nobody opens a
 * chapter to read which model narrated it, and a permanent line saying so is
 * one more thing between the reader and the first sentence — but the question
 * does get asked, by an author comparing two models by ear, who should not
 * have to go looking in a settings screen for it. The studio shows it and the
 * reader does not: the settings of a recording are the author's business.
 *
 * It describes the *recording*, not the book's settings. The two disagree the
 * moment a setting is changed, and until the chapter is narrated again what
 * you are hearing is the old one.
 */
export default function RecordingBadge({ recording }: { recording: Recording }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement | null>(null);
  const close = useCallback(() => setOpen(false), []);

  // The catalogue names the model and, on models narrated in a voice of their
  // own, that voice — so the badge says "ElevenLabs v4" rather than a slug.
  const catalogue = useQuery({
    queryKey: ["voices"],
    queryFn: listVoices,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  useDismiss(open, box, close);

  const model = catalogue.data?.models?.find((m) => m.name === recording.model);
  const modelName = t.voice.models[recording.model] ?? model?.label ?? recording.model;
  // ElevenLabs knows George as `JBFqnCBsd6RMkjVDRZzb`, and the render stores
  // what the vendor was sent (ADR-0109). Shown as the reader's name for it.
  const voiceShown = (name: string) => model?.voices?.find((v) => v.name === name)?.label || name;
  const extras = [
    recording.read_context ? t.player.recordingReadCut : "",
    recording.prepared ? t.player.recordingPrepared : "",
  ].filter(Boolean);

  return (
    <div ref={box} className="relative shrink-0">
      <button
        type="button"
        data-testid="recording-badge"
        aria-label={t.player.recordingTitle}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="grid size-11 place-items-center rounded-full border border-line text-ink-soft transition-colors hover:bg-sunk hover:text-ink"
      >
        <svg viewBox="0 0 24 24" className="size-5" fill="currentColor" aria-hidden="true">
          <path d="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Zm0 1.6a7.4 7.4 0 1 1 0 14.8 7.4 7.4 0 0 1 0-14.8Zm0 2.1a1.1 1.1 0 1 1 0 2.2 1.1 1.1 0 0 1 0-2.2Zm-.9 3.8h1.8v6.7h-1.8Z" />
        </svg>
      </button>
      {open && (
        // Opens downwards, from a badge that sits beside the chapter title,
        // and is the width of the page rather than of its button: these are
        // sentences, not chips. `end-0` so it opens inwards from whichever
        // edge the badge is on — the interface flips (ADR-0023) and a panel
        // pinned to the left runs off the screen in Persian.
        <div
          data-testid="recording-panel"
          className="absolute end-0 top-full z-30 mt-2 w-[min(20rem,calc(100vw-2rem))] rounded-card border border-line bg-surface p-3 shadow-lg"
        >
          <h2 className="mb-2 text-xs font-medium text-muted">{t.player.recordingTitle}</h2>
          <dl className="space-y-1.5 text-sm">
            <Row label={t.player.recordingModel} value={modelName} />
            {/* A model with one voice of its own says so through the catalogue
                (ADR-0109); there is no voice to report separately, because
                naming the model has already named it. */}
            {model?.voices?.length !== 1 && recording.voice && (
              <Row label={t.player.recordingVoice} value={voiceShown(recording.voice)} />
            )}
            <Row
              label={t.player.recordingMode}
              value={
                recording.mode === "economy"
                  ? t.player.recordingModeEconomy
                  : t.player.recordingModeFast
              }
            />
            {recording.part_chars ? (
              <Row
                label={t.player.recordingParts}
                value={t.player.recordingPartsValue(recording.parts, recording.part_chars)}
              />
            ) : (
              <Row label={t.player.recordingParts} value={t.meta.num(recording.parts)} />
            )}
            {/* Only what was on. A list of everything that was *not* done is
                longer than the badge and says less. One row however many there
                are: two rows both labelled "Also" reads as a rendering bug. */}
            {extras.length > 0 && (
              <Row label={t.player.recordingExtras} value={extras.join(" · ")} />
            )}
            <Row
              label={t.player.recordingMade}
              value={new Date(recording.made_at).toLocaleDateString(t.meta.dateLocale, {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            />
          </dl>
          <p className="mt-2 text-xs text-muted">{t.player.recordingNote}</p>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="shrink-0 text-xs text-muted">{label}</dt>
      {/* The voice is a name and the model is a product name, both Latin, in
          what may be a Persian interface. dir="auto" on the value and nowhere
          inside it. */}
      <dd dir="auto" className="min-w-0 text-end">
        {value}
      </dd>
    </div>
  );
}
